/**
 * # Domain API read routes
 *
 * One table that says where every organization-scoped *read* lives on the
 * domain API (`apps/api`), keyed the way the web client names it.
 *
 * ## Why one table
 *
 * Two callers build these URLs:
 *
 * 1. The browser, calling the API directly with a user token
 *    (`apps/web/src/state/domainApi.ts`).
 * 2. The workspace server's `/api/amoreg/regulation/<key>` proxy, which
 *    forwards the same reads with the service credential when the browser
 *    cannot go direct (`apps/server/src/amoreg/governanceHttp.ts`).
 *
 * If those two disagreed about a path, a read would work through one door
 * and 404 through the other — and the fallback in the browser would hide it.
 * Keeping the mapping here means an endpoint moves in exactly one place and
 * both callers follow.
 *
 * ## Contract
 *
 * - Keys are the last segment of the proxy path (`overview`,
 *   `governance-list`, …). The server derives the key from its route path;
 *   the web passes it explicitly.
 * - Every parameter value is URL-encoded **here**, once. Callers pass raw
 *   identifiers and must not pre-encode.
 * - `organizationId` is required for every route; some routes require one
 *   more scoping parameter ({@link RouteSpec.required}). A missing or empty
 *   required value yields `null`, never a path with a hole in it.
 * - Unknown keys yield `null`. The web treats that as "proxy only"; the
 *   server refuses to register such a route at boot.
 *
 * This table is reads only. Writes keep their explicit handlers on the
 * server because several do more than forward (uploads, downloads, proofs).
 *
 * @module platform-shared/domainApiRoutes
 */

/** Raw (unencoded) parameter values keyed by query-parameter name. */
export type DomainApiParams = Readonly<Record<string, string | undefined>>;

/**
 * One read route.
 *
 * @internal
 */
interface RouteSpec {
  /** Scoping parameters that must be present (besides `organizationId`). */
  readonly required: ReadonlyArray<string>;
  /** Builds the path from already-encoded values. */
  readonly path: (params: Readonly<Record<string, string>>) => string;
}

/** `/api/v1/organizations/<id>` — the prefix of every tenant-scoped route. */
const org = (params: Readonly<Record<string, string>>): string =>
  `/api/v1/organizations/${params["organizationId"]}`;

/**
 * An optional query string from the named parameters that are present.
 * Values are re-encoded by `URLSearchParams`, so this receives raw values —
 * see {@link domainApiReadPath}, which withholds them from `encoded`.
 */
const query = (
  raw: DomainApiParams,
  names: ReadonlyArray<string>,
): string => {
  const search = new URLSearchParams();
  for (const name of names) {
    const value = raw[name];
    if (value !== undefined && value.length > 0) search.set(name, value);
  }
  const text = search.toString();
  return text.length === 0 ? "" : `?${text}`;
};

/**
 * The routes. Ordered to mirror the API contract groups (regulation,
 * frameworks, governance, policies, people, monitors) so a reader can check
 * it against `apps/api/src/contracts.ts` top to bottom.
 */
const ROUTES: Readonly<Record<string, RouteSpec>> = {
  // Regulation workspace
  overview: { required: [], path: (p) => `${org(p)}/regulation/overview` },
  "review-counts": { required: [], path: (p) => `${org(p)}/regulation/review-counts` },
  timeline: { required: [], path: (p) => `${org(p)}/regulation/timeline` },

  // Frameworks and regimes
  frameworks: { required: [], path: (p) => `${org(p)}/frameworks` },
  controls: {
    required: ["frameworkId"],
    path: (p) => `${org(p)}/frameworks/${p["frameworkId"]}/controls`,
  },
  "regime-publications": {
    required: ["regimeId"],
    path: (p) => `${org(p)}/frameworks/${p["regimeId"]}/publications`,
  },
  "governance-frameworks": { required: [], path: (p) => `${org(p)}/governance-frameworks` },

  // Policy governance
  "governance-list": { required: [], path: (p) => `${org(p)}/governance/policies` },
  governance: {
    required: ["policyId"],
    path: (p) => `${org(p)}/policies/${p["policyId"]}/governance`,
  },
  mappings: {
    required: ["policyId"],
    path: (p) => `${org(p)}/policies/${p["policyId"]}/mappings`,
  },
  acknowledgments: {
    required: ["policyId"],
    path: (p) => `${org(p)}/policies/${p["policyId"]}/acknowledgments`,
  },
  activity: {
    required: ["policyId"],
    path: (p) => `${org(p)}/policies/${p["policyId"]}/activity`,
  },
  text: { required: ["policyId"], path: (p) => `${org(p)}/policies/${p["policyId"]}/text` },
  owners: { required: ["policyId"], path: (p) => `${org(p)}/policies/${p["policyId"]}/owners` },
  versions: {
    required: ["policyId"],
    path: (p) =>
      p["version"] === undefined
        ? `${org(p)}/policies/${p["policyId"]}/versions`
        : `${org(p)}/policies/${p["policyId"]}/versions/${p["version"]}`,
  },

  // Findings and recommendations (org-wide, or scoped to one policy)
  findings: {
    required: [],
    path: (p) =>
      p["policyId"] === undefined
        ? `${org(p)}/findings`
        : `${org(p)}/policies/${p["policyId"]}/findings`,
  },
  recommendations: {
    required: [],
    // Filters travel as a query string; the API reads `policyId` and `status`.
    path: (p) => `${org(p)}/recommendations${p["__query"] ?? ""}`,
  },

  // Evidence, integrations, monitors
  evidence: { required: [], path: (p) => `${org(p)}/evidence` },
  integrations: { required: [], path: (p) => `${org(p)}/integrations` },
  monitors: { required: [], path: (p) => `${org(p)}/monitors` },
  automations: { required: [], path: (p) => `${org(p)}/automations` },

  // People
  people: { required: [], path: (p) => `${org(p)}/people` },
  "policy-owners": { required: [], path: (p) => `${org(p)}/policy-owners` },

  // Policy library
  policies: { required: [], path: (p) => `${org(p)}/policies` },
  "regulatory-updates": { required: [], path: (p) => `${org(p)}/regulatory-updates` },
  "policy-templates": { required: [], path: (p) => `${org(p)}/policy-templates` },
};

/** Routes whose extra parameters ride in the query string rather than the path. */
const QUERY_PARAMS: Readonly<Record<string, ReadonlyArray<string>>> = {
  recommendations: ["policyId", "status"],
};

/** A key present in the table. Structurally `string`; use {@link isDomainApiReadKey} to check. */
export type DomainApiReadKey = keyof typeof ROUTES;

/**
 * Whether `key` names a read in the table. Own-property check, so
 * prototype names (`toString`, `constructor`) are not keys.
 */
export function isDomainApiReadKey(key: string): key is DomainApiReadKey {
  return Object.prototype.hasOwnProperty.call(ROUTES, key);
}

/**
 * The API path for a read.
 *
 * @param key - The read's name; see {@link ROUTES}.
 * @param params - Raw values keyed by query-parameter name. `organizationId`
 *   is always required. Undefined or empty values count as absent.
 * @returns The path (with query string where the route uses one), or `null`
 *   when the key is unknown or a required parameter is absent.
 *
 * @example
 * ```ts
 * domainApiReadPath("governance", { organizationId: "org-1", policyId: "p/1" });
 * // → "/api/v1/organizations/org-1/policies/p%2F1/governance"
 * domainApiReadPath("governance", { organizationId: "org-1" });
 * // → null (policyId is required)
 * ```
 */
export function domainApiReadPath(key: string, params: DomainApiParams): string | null {
  const spec = ROUTES[key];
  if (spec === undefined) return null;

  const queryNames = QUERY_PARAMS[key] ?? [];
  const encoded: Record<string, string> = {};
  for (const [name, value] of Object.entries(params)) {
    if (value === undefined || value.length === 0) continue;
    // Query-string values are encoded by URLSearchParams in `query`; path
    // values are encoded here. Never both.
    if (!queryNames.includes(name)) encoded[name] = encodeURIComponent(value);
  }
  for (const name of ["organizationId", ...spec.required]) {
    if (encoded[name] === undefined) return null;
  }
  if (queryNames.length > 0) encoded["__query"] = query(params, queryNames);
  return spec.path(encoded);
}
